import type { ClientKind } from "@/platform";

/** launch_app 유입: 웹 = 첫 방문 URL utm, Android = Play Install Referrer, iOS = organic. 첫 실행 때 정해 저장하고 이후 같은 값. */
export type Acquisition = { source: string; campaign: string };

const ORGANIC = "organic";
const FIRST_LAUNCH_KEY = "hamlak.firstLaunchAt";
const ACQ_KEY = "hamlak.acq";

/** 소문자·trim, [a-z0-9_.-] 외 문자는 _ , 최대 64자. 비면 organic */
export function normalizeUtm(raw: string | null | undefined): string {
  const v = (raw ?? "").trim().toLowerCase().replace(/[^a-z0-9_.-]+/g, "_").slice(0, 64);
  return v || ORGANIC;
}

const fromQuery = (query: string): Acquisition => {
  const q = new URLSearchParams(query);
  return { source: normalizeUtm(q.get("utm_source")), campaign: normalizeUtm(q.get("utm_campaign")) };
};

/** Install Referrer 원문(`utm_source=…&utm_campaign=…`) */
export const parseReferrer = (referrer: string): Acquisition => fromQuery(referrer);

export function resolveAcquisition(kind: ClientKind, ctx: { search: string; referrer: string | null }): Acquisition {
  if (kind === "web") return fromQuery(ctx.search);
  if (kind === "android" && ctx.referrer) return parseReferrer(ctx.referrer);
  return { source: ORGANIC, campaign: ORGANIC };
}

// 저장은 localStorage(재설치하면 WebView 저장소가 지워진다 = 첫 실행). 막힌 환경은 매번 첫 실행으로 본다.
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // 저장 불가: 다음 세션도 첫 실행으로 보인다
  }
};

/** 첫 호출이면 true 를 돌려주고 표식을 남긴다 */
export function takeFirstLaunch(now = new Date()): boolean {
  if (read(FIRST_LAUNCH_KEY)) return false;
  write(FIRST_LAUNCH_KEY, now.toISOString());
  return true;
}

export function storedAcquisition(): Acquisition | null {
  const raw = read(ACQ_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<Acquisition>;
    return typeof v.source === "string" && typeof v.campaign === "string" ? { source: v.source, campaign: v.campaign } : null;
  } catch {
    return null;
  }
}

export const storeAcquisition = (a: Acquisition) => write(ACQ_KEY, JSON.stringify(a));

// ---- Android 디퍼드 딥링크(AC0-360) ----
// 웹 /go 가 Play 로 보낼 때 referrer=`plot=<key>&utm_*` 를 싣고, 설치 뒤 첫 부팅이 같은 리퍼러에서 plot 을 읽어 플롯 프로필로 간다.
const DEFERRED_LINK_KEY = "hamlak.deferredLinkDone";

/** Install Referrer 원문 → 디퍼드 딥링크 라우터 경로(`/plots/<key>`). plot 이 없으면 null. */
export function deferredLinkPath(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  const plot = new URLSearchParams(referrer).get("plot")?.trim();
  return plot ? `/plots/${encodeURIComponent(plot)}` : null;
}

/** 디퍼드 딥링크는 설치 뒤 한 번만. 아직 안 썼으면 true 를 돌려주고 표식을 남긴다(재설치 = WebView 저장소 초기화 = 다시 한 번). */
export function takeDeferredLink(): boolean {
  if (read(DEFERRED_LINK_KEY)) return false;
  write(DEFERRED_LINK_KEY, "1");
  return true;
}
